import { supabase } from '../lib/supabase';

const TABLE_NAME = 'calendar_events';

// Get all calendar events for a date range
export const getCalendarEvents = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .gte('start_date', startDate.toISOString())
    .lte('start_date', endDate.toISOString())
    .order('start_date', { ascending: true });

  if (error) throw error;
  
  // Note: assigned_to is just a UUID, we can't join auth.users directly
  // The assigned user info would need to be fetched separately or stored in a users table
  return data || [];
};

// Get all calendar events (no date filter)
export const getAllCalendarEvents = async () => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .order('start_date', { ascending: false });

  if (error) throw error;
  return data || [];
};

// Get calendar events for a specific client
export const getCalendarEventsByClient = async (clientId, options = {}) => {
  let query = supabase
    .from(TABLE_NAME)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .eq('client_id', clientId)
    .order('start_date', { ascending: false });

  if (options.status) {
    query = query.eq('status', options.status);
  }
  if (options.startDate) {
    query = query.gte('start_date', options.startDate.toISOString());
  }
  if (options.endDate) {
    query = query.lte('start_date', options.endDate.toISOString());
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Get calendar events with optional filters (for BMS task history)
export const getCalendarEventsFiltered = async (filters = {}) => {
  let query = supabase
    .from(TABLE_NAME)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .order('start_date', { ascending: false });

  if (filters.clientId) {
    query = query.eq('client_id', filters.clientId);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.startDate) {
    query = query.gte('start_date', filters.startDate.toISOString());
  }
  if (filters.endDate) {
    query = query.lte('start_date', filters.endDate.toISOString());
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Get a single calendar event by ID
export const getCalendarEvent = async (eventId) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .eq('id', eventId)
    .single();

  if (error) throw error;
  return data;
};

// Create a new calendar event
export const createCalendarEvent = async (eventData) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .insert([eventData])
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .single();

  if (error) throw error;
  return data;
};

// Update a calendar event
export const updateCalendarEvent = async (eventId, updates) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .update(updates)
    .eq('id', eventId)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .single();

  if (error) throw error;
  return data;
};

// Delete a calendar event
export const deleteCalendarEvent = async (eventId) => {
  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('id', eventId);

  if (error) throw error;
};

/**
 * Check if a user has an overlapping task at the same time
 * @param {string} assignedTo - User ID to check
 * @param {Date} startDate - Start date/time of the new task
 * @param {Date} endDate - End date/time of the new task (can be null)
 * @param {boolean} allDay - Whether the task is all-day
 * @param {string} excludeEventId - Event ID to exclude from check (for updates)
 * @returns {Promise<Array>} Array of conflicting events
 */
export const checkTaskConflict = async (assignedTo, startDate, endDate, allDay = false, excludeEventId = null) => {
  if (!assignedTo) {
    // No user assigned, no conflict
    return [];
  }

  try {
    // Calculate date range to query (expand by 1 day on each side to catch all-day events)
    const queryStart = new Date(startDate);
    queryStart.setDate(queryStart.getDate() - 1);
    queryStart.setHours(0, 0, 0, 0);
    
    const queryEnd = new Date(endDate || startDate);
    queryEnd.setDate(queryEnd.getDate() + 1);
    queryEnd.setHours(23, 59, 59, 999);

    // Query all non-completed tasks for this user in the date range
    let query = supabase
      .from(TABLE_NAME)
      .select('id, title, start_date, end_date, all_day, status')
      .eq('assigned_to', assignedTo)
      .neq('status', 'completed')
      .gte('start_date', queryStart.toISOString())
      .lte('start_date', queryEnd.toISOString());

    // Exclude the current event if updating
    if (excludeEventId) {
      query = query.neq('id', excludeEventId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error checking task conflict:', error);
      // Don't throw, just return empty array to allow the operation
      return [];
    }

    // Client-side validation for precise overlap detection
    if (data && data.length > 0) {
      const conflicts = data.filter(event => {
        const eventStart = new Date(event.start_date);
        const eventEnd = event.end_date ? new Date(event.end_date) : new Date(eventStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end date
        const newStart = new Date(startDate);
        const newEnd = endDate ? new Date(endDate) : new Date(newStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no end date

        // If either event is all-day, check if they're on the same date
        if (allDay || event.all_day) {
          const newDate = new Date(newStart);
          const eventDate = new Date(eventStart);
          return (
            newDate.getFullYear() === eventDate.getFullYear() &&
            newDate.getMonth() === eventDate.getMonth() &&
            newDate.getDate() === eventDate.getDate()
          );
        }

        // Both are timed events - check for overlap
        // Two time ranges overlap if: start1 < end2 && start2 < end1
        return newStart < eventEnd && eventStart < newEnd;
      });

      return conflicts;
    }

    return [];
  } catch (error) {
    console.error('Error checking task conflict:', error);
    return [];
  }
};
